import { useState } from "react";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper/types";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { AnimatePresence, motion } from "framer-motion";

import { DialogProps } from ".";
import { theme } from "@/theme";
import { CustomButton } from "@/components";
import { useTranslatedMessages } from "@/multiLanguage/i18n";

export function TutorialDialog({ isOpen, closeDialog }: DialogProps) {
    const lang = useTranslatedMessages();
    const [swiper, setSwiper] = useState<SwiperInstance | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const slides = [
        {
            title: lang.pages.tutorial.tutorialTitle1,
            text: lang.pages.tutorial.tutorialText1,
            image: "/tutorial0.png",
        },
        {
            title: lang.pages.tutorial.tutorialTitle2,
            text: lang.pages.tutorial.tutorialText2,
            image: "/tutorial1.png",
        },
        {
            title: lang.pages.tutorial.tutorialTitle3,
            text: lang.pages.tutorial.tutorialText3,
            image: "/tutorial2.png",
        },
    ];

    const isFirstSlide = activeIndex === 0;
    const isLastSlide = activeIndex === slides.length - 1;

    const onForward = () => {
        if (isLastSlide) closeDialog();
        else swiper?.slideNext();
    };

    /**
     * Alla chiusura il contenuto del Dialog viene smontato e lo Swiper riparte
     * dalla prima slide: azzeriamo anche lo stato locale, altrimenti alla
     * riapertura titolo e paginatore resterebbero fermi all'ultima slide vista.
     * Lo facciamo a transizione conclusa per non far "saltare" il titolo
     * mentre la modale sta sfumando.
     */
    const onDialogExited = () => {
        setActiveIndex(0);
        setSwiper(null);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={closeDialog}
            scroll="paper"
            TransitionProps={{ onExited: onDialogExited }}
            PaperProps={{
                sx: {
                    width: "100%",
                    maxWidth: "30rem",
                    margin: ["1rem", "2rem"],
                    borderRadius: "1.25rem",
                    overflow: "hidden",
                },
            }}
        >
            <Stack
                sx={{
                    position: "relative",
                    background: `linear-gradient(90deg,#4395D0,${theme.palette.secondary.dark})`,
                    // Altezza fissa: i titoli occupano da 1 a 3 righe a seconda
                    // della slide e della lingua, ma l'header non deve muoversi.
                    height: ["7.5rem", "7rem"],
                    padding: ["1rem 2.75rem", "1.25rem 3.25rem"],
                    overflow: "hidden",
                    flexShrink: 0,
                }}
                justifyContent={"center"}
                textAlign={"center"}
            >
                <IconButton
                    onClick={closeDialog}
                    aria-label={lang.shared.close}
                    sx={{
                        position: "absolute",
                        top: "0.5rem",
                        right: "0.5rem",
                        color: theme.palette.primary.light,
                    }}
                >
                    <CloseRoundedIcon />
                </IconButton>
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={activeIndex}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                    >
                        <Typography
                            color={theme.palette.primary.light}
                            sx={{
                                fontWeight: 700,
                                fontSize: ["1rem", "1.2rem"],
                                lineHeight: "1.56rem",
                            }}
                        >
                            {slides[activeIndex].title.toUpperCase()}
                        </Typography>
                    </motion.div>
                </AnimatePresence>
            </Stack>

            <DialogContent sx={{ padding: ["1.25rem 1rem 0.5rem", "1.5rem 2rem 0.5rem"] }}>
                {/*
                 * Senza "autoHeight" le slide vengono stirate (align-items: stretch)
                 * all'altezza della più alta: la modale ha così una dimensione fissa,
                 * tagliata sulla slide con il testo più lungo, e non cambia più
                 * altezza passando da una slide all'altra.
                 */}
                <Swiper
                    onSwiper={setSwiper}
                    onActiveIndexChange={(event) => setActiveIndex(event.activeIndex)}
                    style={{ width: "100%" }}
                >
                    {slides.map((slide) => (
                        <SwiperSlide key={slide.image}>
                            <Stack alignItems={"center"} gap={1.5}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: ["7.5rem", "9rem"],
                                        height: ["7.5rem", "9rem"],
                                        flexShrink: 0,
                                        borderRadius: "50%",
                                        backgroundColor: theme.palette.secondary.main,
                                    }}
                                >
                                    <img
                                        src={slide.image}
                                        alt=""
                                        style={{
                                            width: "70%",
                                            height: "70%",
                                            objectFit: "contain",
                                        }}
                                    />
                                </Box>
                                <Typography
                                    align="center"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        fontFamily: "Open Sans",
                                        fontWeight: 400,
                                        fontSize: ["0.85rem", "0.95rem"],
                                        lineHeight: "1.5rem",
                                    }}
                                >
                                    {slide.text}
                                </Typography>
                            </Stack>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </DialogContent>

            <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                gap={1}
                sx={{ padding: ["0 0.75rem 1rem", "0 1.5rem 1.5rem"] }}
            >
                <Button
                    onClick={() => swiper?.slidePrev()}
                    disabled={isFirstSlide}
                    startIcon={<ArrowBackRoundedIcon />}
                    sx={{
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        fontSize: "1rem",
                        color: theme.palette.secondary.dark,
                        // Con sx la regola di default del Mui-disabled non passa.
                        "&.Mui-disabled": { color: theme.palette.secondary.dark, opacity: 0.35 },
                    }}
                >
                    {lang.shared.previous}
                </Button>

                <Stack direction={"row"} gap={0.75} justifyContent={"center"}>
                    {slides.map((slide, index) => (
                        <Box
                            key={slide.image}
                            component="button"
                            aria-label={`${index + 1}/${slides.length}`}
                            onClick={() => swiper?.slideTo(index)}
                            sx={{
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                                height: "0.5rem",
                                width: index === activeIndex ? "1.5rem" : "0.5rem",
                                borderRadius: "0.25rem",
                                transition: "width 0.25s, background-color 0.25s",
                                backgroundColor:
                                    index === activeIndex
                                        ? theme.palette.secondary.dark
                                        : theme.palette.secondary.main,
                            }}
                        />
                    ))}
                </Stack>

                <CustomButton
                    onClick={onForward}
                    sx={{
                        margin: 0,
                        paddingLeft: "1rem",
                        paddingRight: "1rem",
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        color: theme.palette.primary.light,
                        backgroundColor: theme.palette.secondary.dark,
                    }}
                    label={isLastSlide ? lang.shared.understood : lang.shared.next}
                    endIcon={
                        isLastSlide ? <CheckRoundedIcon /> : <ArrowForwardRoundedIcon />
                    }
                />
            </Stack>
        </Dialog>
    );
}
